import React from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import {
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";

import { AssetProfile, Odometer, Usage } from "./data-loader-dialog";

interface TemplateListTableProps {
  assetList: AssetProfile[];
  setAssetList: (newAssetList: AssetProfile[]) => void;
  deleteAssetType: (index: number) => void;
}

const AssetLoaderForm = ({
  assetList,
  setAssetList,
  deleteAssetType,
}: TemplateListTableProps) => {
  const renderTableRows = () => {
    return assetList.map((asset, index) => {
      return (
        <TableRow key={"test"}>
          <TableCell align="center">
            <TextField
              multiline
              name="asset-title-input"
              placeholder="Asset Titles"
              inputProps={{
                "data-testid": "asset-title-input",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => {
                if (e.target.value.trim() === "") {
                  assetList[index].titles = [];
                } else {
                  assetList[index].titles = e.target.value.split(",");
                }
                setAssetList(assetList);
              }}
              value={asset.titles}
            />
          </TableCell>
          <TableCell align="center">
            <TextField
              multiline
              name="asset-type-name-input"
              placeholder="Type Name"
              inputProps={{
                "data-testid": "asset-type-name-input",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => {
                assetList[index].typeName = e.target.value;
                setAssetList(assetList);
              }}
              value={asset.typeName}
            />
          </TableCell>
          <TableCell align="center">
            <TextField
              multiline
              name="asset-prefix-input"
              placeholder="Asset Prefix"
              inputProps={{
                "data-testid": "asset-prefix-input",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => {
                assetList[index].prefix = e.target.value;
                setAssetList(assetList);
              }}
              value={asset.prefix}
            />
          </TableCell>
          <TableCell align="center">
            <TextField
              id="asset-quantity-input"
              value={asset.count.toString()}
              fullWidth
              type="number"
              onChange={(e) => {
                let count = parseInt(e.target.value);
                if (count > 1000) {
                  count = 1000;
                }
                if (Number.isNaN(count) || count < 0) {
                  count = 0;
                }
                assetList[index].count = Math.abs(count);
                setAssetList(assetList);
              }}
              margin="normal"
              variant="outlined"
              InputProps={{
                inputProps: {
                  "data-testid": "asset-quantity-input",
                },
              }}
            />
          </TableCell>
          <TableCell align="center">
            <Switch
              id="asset-include-odometer-switch"
              data-testid="asset-include-odometer-switch"
              checked={asset.includeOdometer}
              onChange={() => {
                assetList[index].includeOdometer = !asset.includeOdometer;
                setAssetList(assetList);
              }}
            />{" "}
          </TableCell>
          <TableCell align="center">
            <Select
              disabled={!asset.includeOdometer}
              defaultValue={Odometer.ODOMETER_UNIT_KILOMETER}
              displayEmpty
              labelId="asset-odometer-unit-selector"
              id="asset-odometer-unit-selector"
              data-testid="asset-odometer-unit-selector"
              value={asset.odometer}
              onChange={(e) => {
                assetList[index].odometer = e.target.value as Odometer;
                setAssetList(assetList);
              }}
            >
              <MenuItem
                value={Odometer.ODOMETER_UNIT_KILOMETER}
                id="odometer-select-kilometer"
                data-testid="odometer-select-kilometer"
              >
                <em>Kilometer</em>
              </MenuItem>
              <MenuItem
                value={Odometer.ODOMETER_UNIT_MILES}
                id="odometer-select-miles"
                data-testid="odometer-select-miles"
                data-anchor="hello"
              >
                Miles
              </MenuItem>
            </Select>
          </TableCell>
          <TableCell align="center">
            <Switch
              checked={asset.includeUsage}
              id="asset-include-usage-switch"
              data-testid="asset-include-usage-switch"
              onChange={() => {
                assetList[index].includeUsage = !asset.includeUsage;
                setAssetList(assetList);
              }}
            />{" "}
          </TableCell>
          <TableCell align="center">
            <Select
              disabled={!asset.includeUsage}
              displayEmpty
              defaultValue={Usage.HOUR}
              labelId="asset-usage-unit-selector"
              id="asset-usage-unit-selector"
              data-testid="asset-usage-unit-selector"
              value={asset.usage}
              onChange={(e) => {
                assetList[index].usage = e.target.value as Usage;
                setAssetList(assetList);
              }}
            >
              <MenuItem value={Usage.HOUR} id="usage-select-hour">
                <em>Hour</em>
              </MenuItem>
              <MenuItem value={Usage.MINUTES} id="usage-select-minute">
                <em>Minute</em>
              </MenuItem>
            </Select>
          </TableCell>
          <TableCell align="center">
            <IconButton
              id="delete-row"
              data-testid="delete-row"
              onClick={() => {
                deleteAssetType(index);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <Grid
      spacing={4}
      container
      direction="row"
      alignItems="center"
      paddingTop={1}
      data-testid="template-list-table"
    >
      <Grid item xs={12}>
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer
            sx={{
              maxHeight: 440,
              border: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <b>Asset Type Details</b>
                  </TableCell>
                  <TableCell colSpan={2} align="center">
                    <b>Odometer</b>
                  </TableCell>
                  <TableCell colSpan={2} align="center">
                    <b>Runtime</b>
                  </TableCell>
                  <TableCell colSpan={1}></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="center">
                    <b>Asset Name</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Asset Type name *</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Asset Prefix *</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Number of Assets *</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Include Odometer</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Unit for Odometer</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Include Runtime</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Unit for Runtime</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Delete row</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{renderTableRows()}</TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default AssetLoaderForm;
